import React, { useEffect, useState } from 'react';
import {
    Box, Checkbox, Chip, FormControlLabel, CircularProgress, FormControl, InputLabel, MenuItem, Paper,
    Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ExpenseClaimService } from '../../services/expense-claim.service';
import { ExpenseClaimDTO, ExpenseClaimStatus } from '../../interfaces/expense.interface';
import { useEmployeeNames } from '../../hooks/useEmployeeNames';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';
import { hideCancelled, sortByDateDesc } from '../../utils/listDefaults';
import { CLAIM_STATUS_COLOR, claimRef, claimStatusLabel, formatAmount } from '../../utils/expenseDisplay';

const STATUSES: (ExpenseClaimStatus | '')[] = ['', 'DRAFT', 'SUBMITTED', 'IN_REVIEW', 'APPROVED', 'REJECTED', 'PAID', 'CANCELLED'];

const AllExpenseClaims: React.FC = () => {
    const [claims, setClaims] = useState<ExpenseClaimDTO[]>([]);
    const employeeName = useEmployeeNames(claims);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<ExpenseClaimStatus | ''>('');
    const [showCancelled, setShowCancelled] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const load = async () => {
        setLoading(true);
        try {
            const page = await ExpenseClaimService.listAll(statusFilter || undefined);
            setClaims(page.content);
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load claims');
        } finally {
            setLoading(false);
        }
    };

    // Newest period first; cancelled claims hidden unless asked for (or picked in the Status filter).
    const visibleClaims = sortByDateDesc(
        hideCancelled(claims, c => c.status, showCancelled || statusFilter === 'CANCELLED'),
        c => c.periodTo);

    return (
        <Box sx={{ p: 3 }} data-help-page="expense.allClaims.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap" spacing={2} sx={{ mb: 2 }} data-help-section="expense.allClaims.filters">
                <Typography variant="h5">All Expense Claims</Typography>
                <FormControlLabel data-help="expense.allClaims.showCancelled"
                    control={<Checkbox size="small" checked={showCancelled} onChange={e => setShowCancelled(e.target.checked)} />}
                    label="Show cancelled"
                />
                <FormControl size="small" sx={{ minWidth: 180 }} data-help="expense.allClaims.status">
                    <InputLabel>Status</InputLabel>
                    <Select
                        label="Status" value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value as ExpenseClaimStatus | '')}
                    >
                        {STATUSES.map(s => (
                            <MenuItem key={s || 'all'} value={s}>{s ? claimStatusLabel(s) : 'All'}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} data-help-section="expense.allClaims.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="expense.allClaims.col.claim">Claim</TableCell>
                                <TableCell data-help="expense.allClaims.col.title">Title</TableCell>
                                <TableCell data-help="expense.allClaims.col.employee">Employee</TableCell>
                                <TableCell data-help="expense.allClaims.col.period">Period</TableCell>
                                <TableCell align="right" data-help="expense.allClaims.col.total">Total</TableCell>
                                <TableCell align="right" data-help="expense.allClaims.col.net">Net</TableCell>
                                <TableCell data-help="expense.allClaims.col.tier">Tier</TableCell>
                                <TableCell data-help="expense.allClaims.col.status">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleClaims.map(c => (
                                <TableRow
                                    key={c.id} hover
                                    onClick={() => navigate(`/expense/claims/${c.id}`)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell data-help="expense.allClaims.col.claim">{claimRef(c.id)}</TableCell>
                                    <TableCell data-help="expense.allClaims.col.title">{c.title}</TableCell>
                                    <TableCell data-help="expense.allClaims.col.employee">{employeeName(c)}</TableCell>
                                    <TableCell data-help="expense.allClaims.col.period">{formatDate(c.periodFrom)} → {formatDate(c.periodTo)}</TableCell>
                                    <TableCell align="right" data-help="expense.allClaims.col.total">{formatAmount(c.totalAmount)}</TableCell>
                                    <TableCell align="right" data-help="expense.allClaims.col.net">{formatAmount(c.netReimbursable)}</TableCell>
                                    <TableCell data-help="expense.allClaims.col.tier">{c.currentApprovalTier ?? '—'}</TableCell>
                                    <TableCell data-help="expense.allClaims.col.status"><Chip size="small" label={claimStatusLabel(c.status)} color={CLAIM_STATUS_COLOR[c.status]} /></TableCell>
                                </TableRow>
                            ))}
                            {visibleClaims.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                            No claims match this filter.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default AllExpenseClaims;
